/**
 * Validation utilities
 */

/**
 * Validate Iranian national code
 */
export const validateNationalCode = (code) => {
    if (!code || code.length !== 10) {
        return false;
    }

    // Check if all digits are the same
    if (/^(\d)\1{9}$/.test(code)) {
        return false;
    }

    // Calculate check digit
    const check = parseInt(code[9]);
    let sum = 0;

    for (let i = 0; i < 9; i++) {
        sum += parseInt(code[i]) * (10 - i);
    }

    const remainder = sum % 11;
    const expectedCheck = remainder < 2 ? remainder : 11 - remainder;

    return check === expectedCheck;
};

/**
 * Validate Iranian mobile number
 */
export const validateMobileNumber = (mobile) => {
    const mobileRegex = /^09\d{9}$/;
    return mobileRegex.test(mobile);
};

/**
 * Validate email
 */
export const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

/**
 * Validate password strength
 * Returns: { valid: boolean, strength: 'weak'|'medium'|'strong', message: string }
 */
export const validatePassword = (password) => {
    if (!password || password.length < 8) {
        return {
            valid: false,
            strength: 'weak',
            message: 'رمز عبور باید حداقل 8 کاراکتر باشد',
        };
    }

    let strength = 0;

    // Check for lowercase
    if (/[a-z]/.test(password)) strength++;

    // Check for uppercase
    if (/[A-Z]/.test(password)) strength++;

    // Check for numbers
    if (/\d/.test(password)) strength++;

    // Check for special characters
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;

    if (strength < 2) {
        return {
            valid: false,
            strength: 'weak',
            message: 'رمز عبور ضعیف است',
        };
    } else if (strength < 3) {
        return {
            valid: true,
            strength: 'medium',
            message: 'رمز عبور متوسط است',
        };
    } else {
        return {
            valid: true,
            strength: 'strong',
            message: 'رمز عبور قوی است',
        };
    }
};

/**
 * Validate file size
 */
export const validateFileSize = (file, maxSizeMB) => {
    if (!file) return false;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    return file.size <= maxSizeBytes;
};

/**
 * Validate file type
 */
export const validateFileType = (file, allowedTypes) => {
    if (!file) return false;
    return allowedTypes.includes(file.type);
};

/**
 * Validate image file
 */
export const validateImageFile = (file, maxSizeMB = 5) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];

    if (!validateFileType(file, allowedTypes)) {
        return {
            valid: false,
            message: 'فرمت فایل باید JPG یا PNG باشد',
        };
    }

    if (!validateFileSize(file, maxSizeMB)) {
        return {
            valid: false,
            message: `حجم فایل نباید بیشتر از ${maxSizeMB} مگابایت باشد`,
        };
    }

    return {
        valid: true,
        message: 'فایل معتبر است',
    };
};

/**
 * Validate video file
 */
export const validateVideoFile = (file, maxSizeMB = 50) => {
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime'];

    if (!validateFileType(file, allowedTypes)) {
        return {
            valid: false,
            message: 'فرمت فایل باید MP4، WebM یا MOV باشد',
        };
    }

    if (!validateFileSize(file, maxSizeMB)) {
        return {
            valid: false,
            message: `حجم فایل نباید بیشتر از ${maxSizeMB} مگابایت باشد`,
        };
    }

    return {
        valid: true,
        message: 'فایل معتبر است',
    };
};

/**
 * Validate PDF file
 */
export const validatePDFFile = (file, maxSizeMB = 10) => {
    const allowedTypes = ['application/pdf'];

    if (!validateFileType(file, allowedTypes)) {
        return {
            valid: false,
            message: 'فرمت فایل باید PDF باشد',
        };
    }

    if (!validateFileSize(file, maxSizeMB)) {
        return {
            valid: false,
            message: `حجم فایل نباید بیشتر از ${maxSizeMB} مگابایت باشد`,
        };
    }

    return {
        valid: true,
        message: 'فایل معتبر است',
    };
};

/**
 * Validate required field
 */
export const validateRequired = (value, fieldName = 'این فیلد') => {
    if (!value || (typeof value === 'string' && value.trim() === '')) {
        return {
            valid: false,
            message: `${fieldName} الزامی است`,
        };
    }

    return {
        valid: true,
        message: '',
    };
};

/**
 * Validate min length
 */
export const validateMinLength = (value, minLength, fieldName = 'این فیلد') => {
    if (!value || value.length < minLength) {
        return {
            valid: false,
            message: `${fieldName} باید حداقل ${minLength} کاراکتر باشد`,
        };
    }

    return {
        valid: true,
        message: '',
    };
};

/**
 * Validate max length
 */
export const validateMaxLength = (value, maxLength, fieldName = 'این فیلد') => {
    if (value && value.length > maxLength) {
        return {
            valid: false,
            message: `${fieldName} نباید بیشتر از ${maxLength} کاراکتر باشد`,
        };
    }

    return {
        valid: true,
        message: '',
    };
};

export default {
    validateNationalCode,
    validateMobileNumber,
    validateEmail,
    validatePassword,
    validateFileSize,
    validateFileType,
    validateImageFile,
    validateVideoFile,
    validatePDFFile,
    validateRequired,
    validateMinLength,
    validateMaxLength,
};
